// biome-ignore lint/style/useNodejsImportProtocol: we use the events module, but biome can't detect it in a monorepo yet
import { EventEmitter } from 'events';
import ws from 'websocket';

import { reportException, Trace } from '@gitbook/util-debug';
import type { MaybePromise } from '@gitbook/util-ts';

export * from './ConnectionPool';

export enum WebSocketState {
    CONNECTING = 'connecting',
    OPEN = 'open',
    CLOSED = 'closed',
    ERRORED = 'errored',
}

export type WebSocketMessage = Uint8Array | string;

/**
 * Websocket wrapper with automatic reconnection.
 */
export class WebSocket {
    public state: WebSocketState = WebSocketState.CLOSED;

    public webSocket: ws.w3cwebsocket | undefined;

    private trace: Trace | null = null;
    private destroyed = false;
    private getSocketURL: () => MaybePromise<URL>;

    private openBackoffDelay = 1000;
    private openBackoffTimer: NodeJS.Timeout | undefined;

    private pendingMessages: WebSocketMessage[] = [];

    private events = new EventEmitter();

    constructor(getSocketURL: () => MaybePromise<URL>) {
        this.getSocketURL = getSocketURL;
    }

    /**
     * Add a listener to be called when a message is received.
     */
    public async open(): Promise<void> {
        this.assertNotDestroyed();

        if (this.webSocket) {
            throw new Error('WebSocket already opened');
        }

        this.trace = new Trace('websocket', { sampleRate: 0 });
        this.clearScheduledReconnection();
        this.setState(WebSocketState.CONNECTING);

        return this.trace.wrap(async () => {
            const url = await this.getSocketURL();

            // Headers can't be passed in WebSocket, we need to use the URL
            const headers = this.trace?.headers();
            if (headers) {
                Object.entries(headers).forEach(([key, value]) => {
                    url.searchParams.append(key, value);
                });
            }

            let errored = false;
            return new Promise<void>((resolve) => {
                const socketUrl = url.toString();
                this.trace?.logger.info('opening WebSocket:', socketUrl);

                if (url.protocol !== 'ws:' && url.protocol !== 'wss:') {
                    throw new Error(
                        `Failed to construct 'WebSocket': The URL's scheme must be either 'ws' or 'wss': got ${socketUrl}`
                    );
                }

                const webSocket = new ws.w3cwebsocket(socketUrl);
                this.webSocket = webSocket;

                try {
                    webSocket.binaryType = 'arraybuffer';
                } catch (_err) {
                    // Ignore, it throws an error in Node.js
                }

                webSocket.onopen = () => {
                    if (this.destroyed) {
                        // If the socket was destroyed before it was opened, we close it
                        webSocket.close();
                        return;
                    }

                    this.trace?.logger.warning('WebSocket opened');
                    this.setState(WebSocketState.OPEN);
                    this.sendPendingMessages();
                    resolve();
                };

                webSocket.onclose = (event) => {
                    if (errored) {
                        const reason = getWebSocketCloseReason(event);
                        this.trace?.logger.error(
                            'WebSocket closed with error:',
                            event.code,
                            reason
                        );
                        if (event.code !== 1006) {
                            // Error code 1006 means that the connection was closed abruptly,
                            // which happens when computers go to sleep or the process put to the brackground.
                            // To prevent noise, we don't track them.
                            reportException(
                                new Error(`Websocket error [${event.code}]: ${reason}`)
                            );
                        }
                    }

                    if (this.destroyed) {
                        return;
                    }

                    const previousState = this.state;
                    this.close(WebSocketState.CLOSED);

                    if (previousState === WebSocketState.CONNECTING) {
                        this.trace?.logger.error(
                            'Websocket closed before initial connection',
                            event
                        );
                    }

                    if (previousState === WebSocketState.OPEN) {
                        this.trace?.logger.warning(
                            'WebSocket unexpectedly closed, reconnecting:',
                            event.code,
                            event.reason
                        );
                        this.scheduleReconnection();
                    }
                };

                webSocket.onerror = (event) => {
                    errored = true;
                    if (this.destroyed) {
                        return;
                    }

                    const previousState = this.state;
                    this.close(WebSocketState.ERRORED);

                    if (previousState === WebSocketState.CONNECTING) {
                        this.trace?.logger.error('Websocket error with initial connection', event);
                    }

                    this.trace?.logger.error('WebSocket error, reconnecting:', event);
                    this.scheduleReconnection();
                };

                webSocket.onmessage = (event) => {
                    if (this.destroyed) {
                        // If the websocket was not correctly closed and we still receive message,
                        // we close it again.
                        webSocket.close();
                        return;
                    }

                    const data = event.data;
                    if (data instanceof ArrayBuffer) {
                        this.events.emit('message', new Uint8Array(data));
                    } else if (typeof data === 'string') {
                        this.events.emit('message', data);
                    }
                };
            });
        });
    }

    /**
     * Close the websocket connection, keeping the state and events intact.
     */
    public close(state: WebSocketState.CLOSED | WebSocketState.ERRORED = WebSocketState.CLOSED) {
        this.assertNotDestroyed();
        this.setState(state);

        if (this.openBackoffTimer) {
            clearTimeout(this.openBackoffTimer);
            this.openBackoffTimer = undefined;
        }

        if (this.webSocket) {
            this.trace?.logger.info('Closing WebSocket');
            this.webSocket.close();
            this.webSocket = undefined;
        }
    }

    /**
     * Destroy the socket, making it unusable.
     */
    public destroy() {
        this.close();
        this.events.removeAllListeners();
        this.destroyed = true;
    }

    /**
     * Reopen the websocket after a delay.
     */
    private scheduleReconnection() {
        if (this.openBackoffTimer) {
            return;
        }

        if (this.webSocket) {
            this.close();
        }

        this.trace?.logger.info('Scheduling reconnection in', this.openBackoffDelay, 'ms');
        this.openBackoffTimer = setTimeout(() => {
            this.open().catch((error) => {
                if (this.destroyed) {
                    throw error;
                }

                this.close(WebSocketState.ERRORED);
                this.trace?.logger.error('Failed to reopen socket', error);
                this.trace?.logger.error('Retrying...');
                this.scheduleReconnection();
            });
            this.openBackoffDelay = Math.min(this.openBackoffDelay * 2, 30000);
        }, this.openBackoffDelay);
    }

    /**
     * Force the scheduled reopen to happen now.
     */
    public async forceScheduledReconnection(): Promise<void> {
        if (!this.openBackoffTimer) {
            throw new Error('No scheduled reconnection');
        }

        await this.open();
    }

    /**
     * Clear the scheduled reopen.
     */
    private clearScheduledReconnection() {
        if (this.openBackoffTimer) {
            clearTimeout(this.openBackoffTimer);
            this.openBackoffTimer = undefined;
        }
    }

    /**
     * Add a listener for a message from the server.
     */
    public listen(event: 'message', callback: (event: WebSocketMessage) => void): () => void;
    public listen(event: 'state', callback: (state: WebSocketState) => void): () => void;
    public listen(event: 'state' | 'message', callback: (event: any) => void): () => void {
        this.assertNotDestroyed();
        this.events.addListener(event, callback);

        return () => {
            this.events.removeListener(event, callback);
        };
    }

    /**
     * Send a message to the server.
     */
    public send(message: WebSocketMessage) {
        this.assertNotDestroyed();

        if (this.state !== WebSocketState.OPEN || !this.webSocket) {
            this.pendingMessages.push(message);
            return;
        }
        this.webSocket.send(message);
    }

    /**
     * Send all pending messages.
     */
    private sendPendingMessages() {
        const webSocket = this.webSocket;
        if (this.state !== WebSocketState.OPEN || !webSocket) {
            return;
        }

        this.pendingMessages.forEach((message) => webSocket.send(message));
        this.pendingMessages = [];
    }

    /**
     * Update the state of the socket.
     */
    private setState(state: WebSocketState) {
        if (this.state === state) {
            return;
        }

        this.trace?.logger.info('socket changing state from', this.state, 'to', state);

        this.state = state;
        this.events.emit('state', state);
    }

    /**
     * Assert that the socket is not destroyed and can still be used.
     */
    private assertNotDestroyed() {
        if (this.destroyed) {
            throw new Error('socket is destroyed');
        }
    }
}

/**
 * https://stackoverflow.com/a/28396165
 */
function getWebSocketCloseReason(event: ws.ICloseEvent): string {
    if (event.code === 1000) {
        return 'Normal closure, meaning that the purpose for which the connection was established has been fulfilled.';
    }
    if (event.code === 1001) {
        return 'An endpoint is "going away", such as a server going down or a browser having navigated away from a page.';
    }
    if (event.code === 1002) {
        return 'An endpoint is terminating the connection due to a protocol error';
    }
    if (event.code === 1003) {
        return 'An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).';
    }
    if (event.code === 1004) {
        return 'Reserved. The specific meaning might be defined in the future.';
    }
    if (event.code === 1005) {
        return 'No status code was actually present.';
    }
    if (event.code === 1006) {
        return 'The connection was closed abnormally, e.g., without sending or receiving a Close control frame';
    }
    if (event.code === 1007) {
        return 'An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [https://www.rfc-editor.org/rfc/rfc3629] data within a text message).';
    }
    if (event.code === 1008) {
        return 'An endpoint is terminating the connection because it has received a message that "violates its policy". This reason is given either if there is no other sutible reason, or if there is a need to hide specific details about the policy.';
    }
    if (event.code === 1009) {
        return 'An endpoint is terminating the connection because it has received a message that is too big for it to process.';
    }
    if (event.code === 1010) {
        // Note that this status code is not used by the server, because it can fail the WebSocket handshake instead.
        return `An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake. <br /> Specifically, the extensions that are needed are: ${event.reason}`;
    }
    if (event.code === 1011) {
        return 'A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.';
    }
    if (event.code === 1015) {
        return "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).";
    }
    return 'Unknown reason';
}
